import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DragAndDrop, IconSpinner, Modal } from '../../../../components';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { handleFileUpload } from '../../../../util/circleHelper';
import { generateVideoThumbnail, updateUploadedVideoPoster } from './VideoUploadModal';
import { getPostMediaCount, getPostMediaKind, validatePostMediaFiles } from './postMediaUpload';

import css from './CreatePostForm.module.css';

const FileInput = React.memo(({ inputRef, onChange, isUploadDisabled }) => (
  <input
    ref={inputRef}
    data-testid="image-file-input"
    type="file"
    accept="image/*,video/*"
    onChange={onChange}
    disabled={isUploadDisabled}
    multiple
    style={{ display: 'none' }}
  />
));

const isHeicFile = file => {
  const fileType = file?.type?.toLowerCase();
  return (
    fileType === 'image/heic' ||
    fileType === 'image/heif' ||
    /\.(heic|heif)$/i.test(file?.name || '')
  );
};

const convertHeicFile = async file => {
  if (!isHeicFile(file)) {
    return file;
  }

  // Keep this browser-only dependency out of the server render path.
  const heic2any = require('heic2any');
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 1,
  });
  const jpegBlob = Array.isArray(converted) ? converted[0] : converted;

  return new File([jpegBlob], (file.name || 'image').replace(/\.(heic|heif)$/i, '.jpg'), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
};

const ImageUploadModal = props => {
  const {
    isImageModal,
    onImageClick,
    form,
    values,
    requestDirectUpload,
    formOldValues,
    setFormOldValues,
    usePortal = false,
  } = props;
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const isLoading = requestDirectUpload || isUploading;
  const isUploadDisabled = !dispatch || isLoading;

  const commitUploads = useCallback(
    successfulUploads => {
      const imageUploads = successfulUploads.filter(upload => upload.kind === 'image');
      const videoUploads = successfulUploads
        .map(upload => {
          if (upload.kind !== 'video') return upload;
          return {
            ...upload,
            previewUrl: URL.createObjectURL(upload.file),
          };
        })
        .filter(upload => upload.kind === 'video');

      const newImages = imageUploads.map(upload => upload.response);
      const newImageFiles = imageUploads.map(upload => ({
        file: upload.file,
        signed_id: upload.response.signed_id,
      }));
      const newVideos = videoUploads.map(upload => upload.response);
      const newVideoFiles = videoUploads.map(upload => ({
        file: upload.previewUrl,
        poster: null,
        signed_id: upload.response.signed_id,
      }));

      if (setFormOldValues && typeof setFormOldValues === 'function') {
        setFormOldValues(previousValues => ({
          ...previousValues,
          image: [...(previousValues?.image || []), ...newImages],
          imagefiles: [...(previousValues?.imagefiles || []), ...newImageFiles],
          video: [...(previousValues?.video || []), ...newVideos],
          videofiles: [...(previousValues?.videofiles || []), ...newVideoFiles],
        }));
      }

      if (form?.change) {
        const currentValues = form.getState?.().values || values || {};
        form.change('image', [...(currentValues.image || []), ...newImages]);
        form.change('imagefiles', [...(currentValues.imagefiles || []), ...newImageFiles]);
        form.change('video', [...(currentValues.video || []), ...newVideos]);
        form.change('videofiles', [...(currentValues.videofiles || []), ...newVideoFiles]);
      }

      videoUploads.forEach(upload => {
        generateVideoThumbnail(upload.previewUrl)
          .then(poster =>
            updateUploadedVideoPoster({
              form,
              setFormOldValues,
              signedId: upload.response.signed_id,
              poster,
            })
          )
          .catch(error => console.error('Video thumbnail generation failed:', error));
      });
    },
    [form, setFormOldValues, values]
  );

  const uploadFiles = useCallback(
    async (selectedFiles, input = null) => {
      const files = Array.from(selectedFiles || []);
      if (!files.length) {
        if (input) input.value = '';
        return;
      }

      const conversionResults = await Promise.allSettled(files.map(convertHeicFile));
      const processedFiles = conversionResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      const conversionErrors = conversionResults
        .filter(result => result.status === 'rejected')
        .map(() => 'Unable to process a HEIC/HEIF photo. Please choose a JPEG or PNG photo.');

      if (!processedFiles.length) {
        setUploadError(conversionErrors.join(' '));
        if (input) input.value = '';
        return;
      }

      const currentFormValues = form?.getState?.().values || values || {};
      const existingCount = Math.max(
        getPostMediaCount(currentFormValues),
        getPostMediaCount(formOldValues)
      );
      const { acceptedFiles, errors: validationErrors } = validatePostMediaFiles({
        files: processedFiles,
        existingCount,
      });

      const initialErrors = [...conversionErrors, ...validationErrors];
      setUploadError(initialErrors.length ? initialErrors.join(' ') : null);
      if (!acceptedFiles.length) {
        if (input) input.value = '';
        return;
      }

      setIsUploading(true);

      try {
        const results = await Promise.allSettled(
          acceptedFiles.map(file => handleFileUpload(file, dispatch))
        );
        const successfulUploads = [];
        const failedFiles = [];

        results.forEach((result, index) => {
          const file = acceptedFiles[index];
          if (result.status === 'fulfilled' && result.value?.signed_id) {
            successfulUploads.push({
              file,
              kind: getPostMediaKind(file),
              response: result.value,
            });
          } else {
            failedFiles.push(file.name);
          }
        });

        if (successfulUploads.length) {
          commitUploads(successfulUploads);
        }

        const uploadErrors = [
          ...conversionErrors,
          ...validationErrors,
          ...(failedFiles.length
            ? [`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`]
            : []),
        ];
        setUploadError(uploadErrors.length ? uploadErrors.join(' ') : null);

        if (successfulUploads.length && !uploadErrors.length) {
          onImageClick();
        }
      } catch (error) {
        console.error('Media upload error:', error);
        setUploadError('An unexpected error occurred during upload. Please try again.');
      } finally {
        if (input) input.value = '';
        setIsUploading(false);
      }
    },
    [commitUploads, dispatch, form, formOldValues, onImageClick, values]
  );

  const handleFileInputChange = useCallback(
    event => uploadFiles(event.target?.files, event.target),
    [uploadFiles]
  );

  const handleChooseFileClick = useCallback(() => {
    if (!isUploadDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploadDisabled]);

  return (
    <Modal
      id="imageUploadModal"
      isOpen={isImageModal}
      onClose={() => {
        if (!isLoading) onImageClick();
      }}
      onManageDisableScrolling={manageDisableScrolling}
      className={css.uploadModals}
      usePortal={usePortal}
    >
      <div className={css.contentWrapper}>
        {isLoading ? (
          <div className={css.loader}>
            <IconSpinner type={true} />
          </div>
        ) : (
          <>
            <DragAndDrop
              handleDrop={uploadFiles}
              acceptedFileType="media"
              onInvalidFile={() => setUploadError('Only image and video files are supported.')}
              disabled={isUploadDisabled}
            >
              <div className={css.uploadWrapper}>
                <div className={css.headingName}>Add photos or videos</div>
                <div className={css.boxUploader}>
                  <div className={css.plusIcon}>
                    <span className={css.icon}>
                      <IconCard brand="upload" />
                    </span>
                  </div>
                  <div className={css.dragText}>Drop photos or videos here to upload</div>
                  <div className={css.fileTypes}>
                    Images up to 20 MB, videos up to 100 MB. Maximum 10 media files.
                  </div>
                  <button
                    type="button"
                    onClick={handleChooseFileClick}
                    className={`${css.chooseFileButton} ${isUploadDisabled ? css.disabled : ''}`}
                    disabled={isUploadDisabled}
                  >
                    Choose files
                  </button>
                  <FileInput
                    inputRef={fileInputRef}
                    onChange={handleFileInputChange}
                    isUploadDisabled={isUploadDisabled}
                  />
                </div>
              </div>
            </DragAndDrop>
            {uploadError ? <div className={css.errorMessage}>{uploadError}</div> : null}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ImageUploadModal;
